import { getApiBaseUrl } from '../../utils/config';
import { isHttpErrorCode, isLoginResultSuccess, LoginResult } from './types';

export const loginByEmail = async (
  email: string,
  password: string,
): Promise<LoginResult> => {
  const response = await fetch(getApiBaseUrl() + '/auth/login/email', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, password }),
  });
  return processLoginResponse(response);
};

export const loginByAuthorizationCode = async (
  authorizationCode: string,
): Promise<LoginResult> => {
  const response = await fetch(
    getApiBaseUrl() + '/auth/login/authorizationCode',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ authorizationCode }),
    },
  );
  return processLoginResponse(response);
};

export const loginByUUID = async (
  uuid: string,
  organizationId: string,
): Promise<LoginResult> => {
  const response = await fetch(getApiBaseUrl() + '/auth/login/uuid', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ uuid, organizationId }),
  });
  return processLoginResponse(response);
};

export const loginByLegacyApiToken = async (
  apiToken: string,
): Promise<LoginResult> => {
  const response = await fetch(getApiBaseUrl() + '/auth/login/apiToken', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ apiToken }),
  });
  return processLoginResponse(response);
};

export const refreshAccessToken = (): Promise<string> => {
  return fetch(getApiBaseUrl() + '/auth/refresh', {
    method: 'POST',
    credentials: 'include',
  })
    .then(async (response) => {
      if (!response.ok) {
        const bodyText = await response.text();
        throw new Error('Failed to refresh access token: ' + bodyText);
      }
      return response.json();
    })
    .then((data) => data.accessToken);
};

type CheckHasValidRefreshTokenResponse =
  | {
      status: 'hasToken';
      accessToken: string;
    }
  | {
      status: 'noToken';
    }
  | {
      status: 'error';
      errorMessage: string;
    };

export const checkHasValidRefreshToken =
  async (): Promise<CheckHasValidRefreshTokenResponse> => {
    try {
      const response = await fetch(getApiBaseUrl() + '/auth/check', {
        method: 'POST',
        credentials: 'include',
      });
      if (response.status === 200) {
        const data = await response.json();
        return data.valid
          ? { status: 'hasToken', accessToken: data.accessToken }
          : { status: 'noToken' };
      }
      const responseText = await response.text();
      return {
        status: 'error',
        errorMessage:
          'Invalid server response. Status: ' +
          response.status +
          '. Body: ' +
          responseText,
      };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      return {
        status: 'error',
        errorMessage: 'Internal error: ' + errorMessage,
      };
    }
  };

export const deleteTokens = (): Promise<void> => {
  return fetch(getApiBaseUrl() + '/auth/refreshToken', {
    method: 'DELETE',
    credentials: 'include',
  }).then((response) => {
    if (!response.ok) {
      throw new Error('Failed to logout');
    }
  });
};

export const sendLoginLinkToCompanyEmail = async (
  email: string,
): Promise<void> => {
  const response = await fetch(
    getApiBaseUrl() + '/auth/login/sendLoginLinkToCompanyEmail',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    },
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error?.message || 'Failed to send login link');
  }
};

const processLoginResponse = async (
  response: Response,
): Promise<LoginResult> => {
  const responseData = await response.json();
  if (response.ok) {
    if (isLoginResultSuccess(responseData)) {
      return responseData;
    } else {
      throw new Error('Invalid response', responseData);
    }
  }
  if (isHttpErrorCode(responseData.error?.code)) {
    return responseData;
  }
  throw new Error('Unknown error', responseData);
};
